import { useState, useEffect } from 'react';
import { teamMemberController } from '../../controllers/teamMemberController';
import { projectController } from '../../controllers/projectController';
import { employeeController } from '../../controllers/employeeController';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, 
  ChevronUp, ChevronDown, Users, TrendingUp, Award, Activity,
  Filter, UserCheck, UserX, Briefcase, BarChart3
} from 'lucide-react';

const TeamMembersPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterProject, setFilterProject] = useState('All');
  const [filterRole, setFilterRole] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    projectId: '',
    employeeId: '',
    role: 'Team Member',
    allocation: 100,
    joinedDate: new Date().toISOString().split('T')[0],
    leftDate: '',
    status: 'Active'
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('joinedDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const itemsPerPage = 10;

  const roles = [
    'Project Manager',
    'Tech Lead',
    'Senior Developer',
    'Developer',
    'Junior Developer',
    'Designer',
    'QA Engineer',
    'Business Analyst',
    'Team Member'
  ];

  useEffect(() => { 
    fetchTeamMembers(); 
    fetchProjects();
    fetchEmployees();
  }, []);

  useEffect(() => {
    let result = [...teamMembers];
    
    if (searchQuery) {
      result = result.filter(m => {
        const employee = getEmployeeById(m.employeeId);
        const project = getProjectById(m.projectId);
        return (
          (employee?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (employee?.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (project?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (m.role || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(m => m.status === filterStatus);
    }
    
    if (filterProject !== 'All') {
      result = result.filter(m => {
        const projectId = m.projectId?._id || m.projectId;
        return projectId === filterProject;
      });
    }

    if (filterRole !== 'All') {
      result = result.filter(m => m.role === filterRole);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredMembers(result);
    setCurrentPage(1);
  }, [teamMembers, searchQuery, filterStatus, filterProject, filterRole, sortField, sortDirection]);

  const fetchTeamMembers = async () => {
    setLoading(true);
    const result = await teamMemberController.getAll();
    if (result.success) {
      setTeamMembers(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    const result = await projectController.getAll();
    if (result.success) {
      setProjects(result.data);
    }
  };

  const fetchEmployees = async () => {
    const result = await employeeController.getAll();
    if (result.success) {
      setEmployees(result.data);
    }
  };

  const getEmployeeById = (employeeId) => {
    if (typeof employeeId === 'object' && employeeId !== null) {
      return employeeId;
    }
    return employees.find(e => (e.id || e._id) === employeeId);
  };

  const getProjectById = (projectId) => {
    if (typeof projectId === 'object' && projectId !== null) {
      return projectId;
    }
    return projects.find(p => (p.id || p._id) === projectId);
  };

  const getEmployeeName = (employeeId) => {
    const employee = getEmployeeById(employeeId);
    return employee ? (employee.name || employee.fullName || employee.email || 'Unknown Employee') : 'Unknown Employee';
  };

  const getProjectName = (projectId) => {
    const project = getProjectById(projectId);
    return project ? (project.name || project.projectName || 'Unknown Project') : 'Unknown Project';
  };

  const getStatusColor = (status) => {
    const colors = { 'Active': '#10b981', 'Inactive': '#ef4444' };
    return colors[status] || '#6b7280';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Project Manager': '#8b5cf6',
      'Tech Lead': '#3b82f6',
      'Senior Developer': '#0ea5e9',
      'Developer': '#10b981',
      'Junior Developer': '#84cc16',
      'Designer': '#f59e0b',
      'QA Engineer': '#ef4444',
      'Business Analyst': '#ec4899',
      'Team Member': '#64748b'
    };
    return colors[role] || '#64748b';
  };

  const getCompletionRate = (member) => {
    if (member.tasksAssigned === 0) return 0;
    return Math.round((member.tasksCompleted / member.tasksAssigned) * 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      projectId: '',
      employeeId: '',
      role: 'Team Member',
      allocation: 100,
      joinedDate: new Date().toISOString().split('T')[0],
      leftDate: '',
      status: 'Active'
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    if (!dataToSend.leftDate || dataToSend.leftDate === '') {
      delete dataToSend.leftDate;
    }
    
    const result = await teamMemberController.create(dataToSend);
    if (result.success) {
      setShowAddModal(false);
      fetchTeamMembers();
      alert('✅ Team member added successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    
    let projectIdValue = '';
    if (member.projectId) {
      if (typeof member.projectId === 'object' && (member.projectId.id || member.projectId._id)) {
        projectIdValue = member.projectId.id || member.projectId._id;
      } else if (typeof member.projectId === 'string') {
        projectIdValue = member.projectId;
      }
    }

    let employeeIdValue = '';
    if (member.employeeId) {
      if (typeof member.employeeId === 'object' && (member.employeeId.id || member.employeeId._id)) {
        employeeIdValue = member.employeeId.id || member.employeeId._id;
      } else if (typeof member.employeeId === 'string') {
        employeeIdValue = member.employeeId;
      }
    }
    
    setFormData({
      projectId: projectIdValue,
      employeeId: employeeIdValue,
      role: member.role || 'Team Member',
      allocation: member.allocation || 100,
      joinedDate: member.joinedDate?.split('T')[0] || '',
      leftDate: member.leftDate?.split('T')[0] || '',
      status: member.status || 'Active'
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const dataToSend = { ...formData };
    if (!dataToSend.leftDate || dataToSend.leftDate === '') {
      delete dataToSend.leftDate;
    }
    
    const result = await teamMemberController.update(selectedMember.id || selectedMember._id, dataToSend);
    if (result.success) {
      setShowEditModal(false);
      fetchTeamMembers();
      alert('✅ Team member updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (member) => {
    setSelectedMember(member);
    setShowViewModal(true);
  };

  const handleDelete = async (member) => {
    try {
      const employeeName = getEmployeeName(member.employeeId);
      const isConfirmed = window.confirm(
        `Remove ${employeeName} from team?`
      );
  
      if (!isConfirmed) return;
  
      const result = await teamMemberController.delete(member.id || member._id);
  
      if (result?.success) {
        await fetchTeamMembers();
        alert('✅ Team member removed successfully!');
      } else {
        alert(result?.message || '❌ Failed to remove team member');
      }
    } catch (error) {
      console.error('Delete team member error:', error);
      alert('❌ Something went wrong while removing team member');
    }
  };

  const handleStatusChange = async (member, newStatus) => {
    const result = await teamMemberController.updateStatus(member.id || member._id, newStatus);
    if (result.success) {
      fetchTeamMembers();
      alert('✅ Status updated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterProject !== 'All') filters.projectId = filterProject;
    if (filterRole !== 'All') filters.role = filterRole;
    
    const result = await teamMemberController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `team-members-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Team members exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMembers.length === currentMembers.length && currentMembers.length > 0) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(currentMembers.map(m => m.id || m._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMembers.length === 0) {
      alert('Please select team members to remove');
      return;
    }
    
    const confirmed = window.confirm(`Remove ${selectedMembers.length} selected team members?`);
    if (!confirmed) return;
    
    const result = await teamMemberController.bulkDelete(selectedMembers);
    if (result.success) {
      setSelectedMembers([]);
      fetchTeamMembers();
      alert('✅ Team members removed successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedMembers.length === 0) {
      alert('Please select team members to update');
      return;
    }
    
    const result = await teamMemberController.bulkUpdate(selectedMembers, { status: newStatus });
    if (result.success) {
      setSelectedMembers([]);
      fetchTeamMembers();
      alert('✅ Team members updated successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMembers = filteredMembers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  const stats = {
    total: teamMembers.length,
    active: teamMembers.filter(m => m.status === 'Active').length,
    inactive: teamMembers.filter(m => m.status === 'Inactive').length,
    avgAllocation: teamMembers.length > 0 
      ? Math.round(teamMembers.reduce((sum, m) => sum + (m.allocation || 0), 0) / teamMembers.length)
      : 0
  };

  const styles = {
    container: { padding: 0 },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '24px', 
      flexWrap: 'wrap', 
      gap: '16px' 
    },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      padding: '10px 20px', 
      backgroundColor: '#3b82f6', 
      color: '#fff', 
      border: 'none', 
      borderRadius: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      cursor: 'pointer' 
    },
    secondaryButton: { 
      backgroundColor: '#fff', 
      color: '#1e293b', 
      border: '1px solid #e2e8f0' 
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: '#fff'
    },
    statsBar: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '16px', 
      marginBottom: '24px' 
    },
    statCard: { 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
    },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '20px', 
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { 
      width: '100%', 
      padding: '10px 10px 10px 40px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    searchIcon: { 
      position: 'absolute', 
      left: '12px', 
      top: '50%', 
      transform: 'translateY(-50%)' 
    },
    select: { 
      padding: '10px 16px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      backgroundColor: '#fff', 
      cursor: 'pointer' 
    },
    card: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
      overflow: 'hidden' 
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      backgroundColor: '#f8fafc', 
      padding: '16px', 
      textAlign: 'left', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#475569', 
      borderBottom: '1px solid #e2e8f0', 
      cursor: 'pointer', 
      userSelect: 'none' 
    },
    td: { 
      padding: '16px', 
      borderBottom: '1px solid #e2e8f0', 
      fontSize: '14px', 
      color: '#1e293b' 
    },
    badge: { 
      display: 'inline-block', 
      padding: '4px 12px', 
      borderRadius: '12px', 
      fontSize: '12px', 
      fontWeight: '500' 
    },
    actionButton: { 
      padding: '6px', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderRadius: '6px', 
      cursor: 'pointer' 
    },
    pagination: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '20px', 
      borderTop: '1px solid #e2e8f0' 
    },
    modal: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      padding: '20px' 
    },
    modalContent: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      width: '100%', 
      maxWidth: '700px', 
      maxHeight: '90vh', 
      overflow: 'auto' 
    },
    modalHeader: { 
      padding: '20px 24px', 
      borderBottom: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { 
      display: 'block', 
      marginBottom: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    errorList: { 
      backgroundColor: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: '8px', 
      padding: '12px 16px', 
      marginBottom: '20px' 
    },
    errorItem: { 
      color: '#dc2626', 
      fontSize: '14px', 
      marginBottom: '4px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' 
    },
    modalFooter: { 
      padding: '16px 24px', 
      borderTop: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '12px' 
    },
    progressBar: { 
      width: '100%', 
      height: '8px', 
      backgroundColor: '#e2e8f0', 
      borderRadius: '4px', 
      overflow: 'hidden' 
    },
    progressFill: { 
      height: '100%', 
      backgroundColor: '#3b82f6', 
      transition: 'width 0.3s' 
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Team Members</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {selectedMembers.length > 0 && (
            <>
              <button 
                style={{ ...styles.button, ...styles.dangerButton }} 
                onClick={handleBulkDelete}
              >
                <Trash2 size={18} /> Remove ({selectedMembers.length})
              </button>
              <select 
                style={styles.select}
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusUpdate(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Update Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </>
          )}
          <button 
            style={{ ...styles.button, ...styles.secondaryButton }} 
            onClick={handleExport}
          >
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Member
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Members</div>
            <Users size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Active</div>
            <UserCheck size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.active}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Inactive</div>
            <UserX size={24} color="#ef4444" />
          </div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.inactive}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Avg Allocation</div>
            <Activity size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.avgAllocation}%</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search team members..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <select 
          style={styles.select} 
          value={filterProject} 
          onChange={(e) => setFilterProject(e.target.value)}
        >
          <option value="All">All Projects</option>
          {projects.map(project => (
            <option key={project.id || project._id} value={project.id || project._id}>
              {project.name || project.projectName}
            </option>
          ))}
        </select>
        <select 
          style={styles.select} 
          value={filterRole} 
          onChange={(e) => setFilterRole(e.target.value)}
        >
          <option value="All">All Roles</option>
          {roles.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
        <select 
          style={styles.select} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading team members...
          </div>
        ) : currentMembers.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No team members found. Click "Add Member" to add one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px' }}>
                    <input 
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedMembers.length === currentMembers.length && currentMembers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={styles.th} onClick={() => handleSort('employeeId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Employee {sortField === 'employeeId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Allocation</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Performance</th>
                  <th style={styles.th}>Joined Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentMembers.map((member) => {
                  const completionRate = getCompletionRate(member);
                  
                  return (
                    <tr key={member.id || member._id} style={{ cursor: 'pointer' }}>
                      <td style={styles.td}>
                        <input 
                          type="checkbox"
                          style={styles.checkbox}
                          checked={selectedMembers.includes(member.id || member._id)}
                          onChange={() => handleSelectMember(member.id || member._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={styles.td} onClick={() => handleView(member)}>
                        <div style={{ fontWeight: '500' }}>{getEmployeeName(member.employeeId)}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {getEmployeeById(member.employeeId)?.email || ''}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px' }}>
                          {getProjectName(member.projectId)}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${getRoleColor(member.role)}20`, 
                          color: getRoleColor(member.role) 
                        }}>
                          {member.role}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ marginBottom: '4px', fontSize: '12px', color: '#64748b' }}>
                          {member.allocation}%
                        </div>
                        <div style={styles.progressBar}>
                          <div style={{ 
                            ...styles.progressFill, 
                            width: `${member.allocation}%`,
                            backgroundColor: member.allocation > 100 ? '#ef4444' : '#3b82f6'
                          }} />
                        </div>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={{
                            ...styles.badge,
                            backgroundColor: `${getStatusColor(member.status)}20`,
                            color: getStatusColor(member.status),
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          value={member.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(member, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                          {member.tasksCompleted}/{member.tasksAssigned} tasks
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {completionRate}% completion
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500' }}>
                          {member.joinedDate ? new Date(member.joinedDate).toLocaleDateString() : 'N/A'}
                        </div>
                        {member.leftDate && (
                          <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                            Left: {new Date(member.leftDate).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleView(member); }} 
                            title="View"
                          >
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleEdit(member); }} 
                            title="Edit"
                          >
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleDelete(member); }} 
                            title="Delete"
                          >
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredMembers.length)} of {filteredMembers.length} members
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {showAddModal ? 'Add Team Member' : 'Edit Team Member'}
              </h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project *</label>
                    <select 
                      name="projectId" 
                      style={styles.input} 
                      value={formData.projectId} 
                      onChange={handleInputChange} 
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(project => (
                        <option key={project.id || project._id} value={project.id || project._id}>
                          {project.name || project.projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Employee *</label>
                    <select 
                      name="employeeId" 
                      style={styles.input} 
                      value={formData.employeeId} 
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Employee</option>
                      {employees.map(employee => (
                        <option key={employee.id || employee._id} value={employee.id || employee._id}>
                          {employee.name || employee.fullName} {employee.email ? `(${employee.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Role</label>
                    <select 
                      name="role" 
                      style={styles.input} 
                      value={formData.role} 
                      onChange={handleInputChange}
                    >
                      {roles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Allocation (%) *</label>
                    <input 
                      type="number" 
                      name="allocation" 
                      style={styles.input} 
                      value={formData.allocation} 
                      onChange={handleInputChange} 
                      min="0" 
                      max="100"
                      required
                    />
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      Percentage of time allocated to this project (0-100%)
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select 
                      name="status" 
                      style={styles.input} 
                      value={formData.status} 
                      onChange={handleInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Joined Date *</label>
                    <input 
                      type="date" 
                      name="joinedDate" 
                      style={styles.input} 
                      value={formData.joinedDate} 
                      onChange={handleInputChange} 
                      required
                    />
                  </div>

                  {formData.status === 'Inactive' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Left Date</label>
                      <input 
                        type="date" 
                        name="leftDate" 
                        style={styles.input} 
                        value={formData.leftDate} 
                        onChange={handleInputChange}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Add Member' : 'Update Member')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedMember && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Team Member Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Employee Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getEmployeeName(selectedMember.employeeId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getEmployeeById(selectedMember.employeeId)?.email || 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {getProjectName(selectedMember.projectId)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Role</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${getRoleColor(selectedMember.role)}20`, 
                    color: getRoleColor(selectedMember.role) 
                  }}>
                    {selectedMember.role}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${getStatusColor(selectedMember.status)}20`, 
                    color: getStatusColor(selectedMember.status) 
                  }}>
                    {selectedMember.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Joined Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedMember.joinedDate ? new Date(selectedMember.joinedDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                {selectedMember.leftDate && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Left Date</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#ef4444' }}>
                      {new Date(selectedMember.leftDate).toLocaleDateString()}
                    </div>
                  </div>
                )}

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Allocation</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', marginBottom: '8px' }}>
                    {selectedMember.allocation}%
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ 
                      ...styles.progressFill, 
                      width: `${selectedMember.allocation}%`,
                      backgroundColor: selectedMember.allocation > 100 ? '#ef4444' : '#3b82f6'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Tasks Assigned</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                    {selectedMember.tasksAssigned}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Tasks Completed</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                    {selectedMember.tasksCompleted}
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Completion Rate</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', marginBottom: '8px' }}>
                    {getCompletionRate(selectedMember)}%
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ 
                      ...styles.progressFill, 
                      width: `${getCompletionRate(selectedMember)}%`,
                      backgroundColor: '#10b981'
                    }} />
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedMember); }}
              >
                <Edit2 size={16} /> Edit Member
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembersPage;